import React, { useState } from 'react';
import { motion } from 'framer-motion';
import FaqAccordion, { FaqItem } from '../components/faq/FaqAccordion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons';

const Faq: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');

  // FAQ data with categories
  const faqs: FaqItem[] = [
    {
      question: "Quels types de toitures pouvez-vous installer ou réparer ?",
      answer: "Chez GCM FRANCE, nous intervenons sur tous types de toitures : tuiles (terre cuite, béton, ardoise), zinc, ardoise naturelle, bac acier, toiture terrasse, membrane EPDM ou bitumineuse. Notre expertise couvre aussi bien les toitures traditionnelles que les solutions contemporaines, pour les bâtiments résidentiels, commerciaux ou industriels. Nous adaptons notre approche selon l'architecture de votre bâtiment et vos préférences esthétiques.",
      category: "services"
    },
    {
      question: "Quelle est la durée de vie moyenne d'une toiture ?",
      answer: "La durée de vie d'une toiture dépend principalement du matériau utilisé : les tuiles en terre cuite peuvent durer 50 à 100 ans, l'ardoise naturelle 80 à 100 ans, le zinc 50 à 100 ans, les bardeaux bitumineux 15 à 30 ans, et les membranes d'étanchéité 20 à 40 ans. Cette longévité est conditionnée par la qualité de l'installation initiale, l'entretien régulier et les conditions climatiques locales. Un entretien préventif permet d'optimiser la durabilité de votre toiture.",
      category: "durabilité"
    },
    {
      question: "Qu'est-ce que la garantie décennale et comment s'applique-t-elle à mes travaux de toiture ?",
      answer: "La garantie décennale est une assurance obligatoire qui couvre pendant 10 ans les dommages qui compromettent la solidité de l'ouvrage ou le rendent impropre à sa destination. Pour une toiture, elle couvre notamment les problèmes d'étanchéité, d'affaissement ou de charpente. Chez GCM FRANCE, tous nos travaux de couverture sont couverts par cette garantie, vous assurant une tranquillité d'esprit à long terme. Nous vous fournissons systématiquement une attestation d'assurance décennale avant le début des travaux.",
      category: "garanties"
    },
    {
      question: "Comment se déroule un devis pour des travaux de toiture ?",
      answer: "Notre processus de devis commence par une visite technique gratuite de votre toiture. Notre expert évalue l'état général, identifie les problèmes éventuels et discute avec vous de vos besoins. Dans les 48h, nous vous envoyons un devis détaillé précisant les matériaux, les techniques utilisées, les délais d'intervention et le coût total. Le devis inclut également les garanties applicables et les conditions de paiement. Nous restons disponibles pour répondre à toutes vos questions et ajuster le devis si nécessaire.",
      category: "devis"
    },
    {
      question: "Quelles aides financières puis-je obtenir pour rénover ma toiture ?",
      answer: "Plusieurs aides financières sont disponibles pour la rénovation de toiture, notamment lorsqu'elle améliore l'efficacité énergétique : MaPrimeRénov', éco-prêt à taux zéro, TVA à taux réduit (5,5% ou 10% selon les travaux), aides des collectivités locales, et certificats d'économie d'énergie (CEE). Notre équipe peut vous accompagner dans vos démarches administratives pour constituer vos dossiers de demande d'aide et optimiser vos financements.",
      category: "financement"
    },
    {
      question: "Proposez-vous des solutions de toiture écologique ?",
      answer: "Oui, nous proposons plusieurs solutions de toiture écologique : toitures végétalisées, installation de panneaux solaires intégrés, utilisation de matériaux recyclés ou recyclables, récupération des eaux de pluie, et isolation renforcée avec des matériaux biosourcés (laine de bois, ouate de cellulose, etc.). Ces solutions contribuent à réduire l'empreinte carbone de votre bâtiment tout en améliorant son efficacité énergétique et son confort thermique.",
      category: "écologie"
    },
    {
      question: "Comment reconnaître qu'une toiture a besoin d'être réparée ou remplacée ?",
      answer: "Plusieurs signes indiquent qu'une toiture nécessite une intervention : tuiles cassées, fissurées ou manquantes, présence de mousse ou de végétation, affaissement visible, traces d'humidité ou moisissures dans les combles, infiltrations d'eau, gouttières endommagées ou obstruées, et isolation détériorée. Si votre toiture a plus de 20-30 ans, une inspection préventive est recommandée même en l'absence de signes visibles de détérioration.",
      category: "entretien"
    },
    {
      question: "Comment se déroule un chantier de réfection de toiture ?",
      answer: "Un chantier de réfection de toiture se déroule en plusieurs étapes : installation des échafaudages et protections, dépose de l'ancienne couverture, inspection et réparation éventuelle de la charpente, mise en place du pare-pluie et de l'isolation si nécessaire, pose de la nouvelle couverture, installation des éléments de finition (faîtage, solins, gouttières), et nettoyage complet du chantier. Nous organisons le chantier pour minimiser les nuisances et respecter les délais annoncés.",
      category: "chantier"
    },
    {
      question: "Faut-il un permis pour refaire sa toiture ?",
      answer: "Les démarches administratives dépendent de la nature des travaux. Une simple réparation à l'identique ne nécessite généralement pas d'autorisation. En revanche, une modification de l'aspect extérieur (changement de matériau, de couleur, ajout de fenêtres de toit) requiert une déclaration préalable de travaux. Dans certains cas (modification de la structure, surélévation), un permis de construire peut être nécessaire. Nous vous accompagnons dans ces démarches administratives pour garantir la conformité de votre projet.",
      category: "administratif"
    },
    {
      question: "Pouvez-vous intervenir en urgence en cas de fuite ou de dégât sur ma toiture ?",
      answer: "Oui, GCM FRANCE dispose d'une équipe dédiée aux interventions d'urgence pour les problèmes de toiture (fuites, dégâts suite à une tempête, chute d'arbre, etc.). Nous intervenons rapidement pour sécuriser votre toiture et limiter les dégâts, puis proposons une solution pérenne adaptée à votre situation. En cas d'urgence, contactez notre numéro dédié pour une prise en charge prioritaire de votre demande.",
      category: "urgence"
    },
    {
      question: "Comment assurez-vous la qualité de vos travaux de couverture ?",
      answer: "La qualité est au cœur de notre démarche. Nous sélectionnons rigoureusement nos matériaux auprès de fabricants reconnus, employons des couvreurs qualifiés et expérimentés, et appliquons des processus de contrôle à chaque étape du chantier. Un chef de chantier supervise quotidiennement l'avancement et la qualité d'exécution. Avant la réception des travaux, nous effectuons des tests d'étanchéité pour garantir la parfaite réalisation de votre toiture.",
      category: "qualité"
    },
    {
      question: "Quelles sont les conditions de paiement pour vos travaux de toiture ?",
      answer: "Pour les chantiers de couverture, nous proposons généralement un échelonnement du paiement : un acompte de 30% à la signature du devis, un paiement intermédiaire de 40% au démarrage des travaux, et le solde de 30% à la réception du chantier après validation de la conformité des travaux. Nous acceptons les paiements par virement bancaire et chèque. Pour les projets importants, des facilités de paiement peuvent être envisagées.",
      category: "financement"
    },
    {
      question: "Quelles autres garanties offrez-vous en plus de la garantie décennale ?",
      answer: "En plus de la garantie décennale, nous proposons la garantie de parfait achèvement (1 an) qui couvre les malfaçons et désordres signalés lors de la réception ou pendant l'année qui suit, et la garantie biennale (2 ans) qui couvre les équipements dissociables du bâti. Nous souscrivons également une assurance dommages-ouvrage pour les chantiers importants. Notre engagement qualité inclut aussi un suivi après-chantier et une assistance technique pendant toute la durée des garanties.",
      category: "garanties"
    },
    {
      question: "Proposez-vous un service d'entretien régulier pour les toitures ?",
      answer: "Oui, nous proposons des contrats d'entretien préventif pour prolonger la durée de vie de votre toiture. Ces contrats incluent une visite annuelle ou bisannuelle pour inspecter l'état général de la toiture, nettoyer les gouttières, éliminer la mousse et les débris, vérifier les points sensibles (solins, faîtage, cheminées) et effectuer les petites réparations nécessaires. Un entretien régulier permet d'identifier les problèmes potentiels avant qu'ils ne deviennent graves et coûteux.",
      category: "entretien"
    },
    {
      question: "Quelles sont les tendances actuelles en matière de toiture ?",
      answer: "Les tendances actuelles en matière de toiture s'orientent vers des solutions alliant esthétique, durabilité et performance énergétique : toitures végétalisées, toitures-terrasses accessibles, intégration de panneaux solaires, matériaux à haute réflectivité pour limiter la surchauffe, systèmes de récupération d'eau de pluie, et isolation renforcée. Les matériaux traditionnels comme l'ardoise et la tuile restent populaires mais évoluent avec des formats plus grands et des systèmes de pose simplifiés.",
      category: "tendances"
    }
  ];

  // Get unique categories
  const categories = ['all', ...Array.from(new Set(faqs.map(faq => faq.category)))];

  // Filter FAQs based on search term
  const filteredFaqs = faqs.filter(faq => 
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || 
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="pt-16 bg-gray-50">
      {/* Hero Section */}
      <div className="relative bg-primary-dark py-16 md:py-24">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark to-primary-light opacity-90"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Questions Fréquentes
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              Trouvez des réponses aux questions les plus courantes sur nos services de couverture, nos garanties et notre méthode de travail.
            </p>
          </motion.div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        {/* Search Bar */}
        <div className="relative mb-10">
          <input
            type="text"
            placeholder="Rechercher une question..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-3 pl-10 pr-10 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-dark focus:border-transparent"
          />
          <FontAwesomeIcon 
            icon={faSearch} 
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>

        {/* Category Tabs */}
        <div className="mb-10">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  activeCategory === category
                    ? 'bg-primary text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* FAQ Accordion */}
        <div>
          {filteredFaqs
            .filter(faq => activeCategory === 'all' || faq.category === activeCategory)
            .map((faq, index) => (
              <FaqAccordion 
                key={index} 
                faq={faq} 
              />
            ))}
          
          {filteredFaqs.filter(faq => activeCategory === 'all' || faq.category === activeCategory).length === 0 && (
            <div className="text-center py-10">
              <p className="text-gray-600 mb-4">Aucune question ne correspond à votre recherche.</p>
              <button 
                onClick={() => {setSearchTerm(''); setActiveCategory('all');}}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
              >
                Réinitialiser la recherche
              </button>
            </div>
          )}
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gray-100 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Vous avez d'autres questions ?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Notre équipe est à votre disposition pour répondre à toutes vos interrogations.
          </p>
          <a 
            href="/#/contact" 
            className="inline-block px-6 py-3 bg-primary hover:bg-primary-dark text-white font-medium rounded-md transition-colors"
          >
            Contactez-nous
          </a>
        </div>
      </div>
    </div>
  );
};

export default Faq;
